import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, Clock, X, TrendingUp, MapPin, Loader2, UtensilsCrossed } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEARCH_HISTORY_KEY = 'lemo_search_history';
const MAX_HISTORY_ITEMS = 10;

// Popular searches (can be dynamic from backend later)
const POPULAR_SEARCHES = [
  'Burger',
  'Pizza',
  'Sushi',
  'Salade',
  'Poke Bowl',
  'Tacos'
];

export function SearchBar({ 
  searchQuery, 
  setSearchQuery, 
  cityFilter, 
  setCityFilter, 
  onSearch,
  restaurants = [],
  cuisineTypes = []
}) {
  const navigate = useNavigate();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [dishSuggestions, setDishSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [allRestaurants, setAllRestaurants] = useState([]);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Load search history from localStorage
  useEffect(() => {
    const history = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (history) {
      try {
        setSearchHistory(JSON.parse(history));
      } catch {
        setSearchHistory([]);
      }
    }
  }, []);

  // Fetch all restaurants once for suggestions (without filters)
  useEffect(() => {
    const fetchAllRestaurants = async () => {
      try {
        const response = await axios.get(`${API}/restaurants`);
        setAllRestaurants(response.data);
      } catch (error) {
        console.error('Error fetching restaurants for suggestions:', error);
        // Fallback to passed restaurants
        setAllRestaurants(restaurants);
      }
    };
    fetchAllRestaurants();
  }, [restaurants]);

  // Generate suggestions based on query with debounce
  useEffect(() => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchQuery.length < 1) {
      setSuggestions([]);
      setDishSuggestions([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    // Debounce the search
    debounceRef.current = setTimeout(async () => {
      const query = searchQuery.toLowerCase().trim();
      const newSuggestions = [];

      // Use allRestaurants for suggestions (all restaurants, not filtered)
      const restaurantsToSearch = allRestaurants.length > 0 ? allRestaurants : restaurants;

      // Match restaurants by name (priority)
      const matchedRestaurants = restaurantsToSearch
        .filter(r => r.name.toLowerCase().includes(query))
        .slice(0, 5)
        .map(r => ({ type: 'restaurant', text: r.name, id: r.id, city: r.city }));
      
      newSuggestions.push(...matchedRestaurants);

      // Also match by city
      const matchedByCity = restaurantsToSearch
        .filter(r => r.city?.toLowerCase().includes(query) && !matchedRestaurants.find(m => m.id === r.id))
        .slice(0, 3)
        .map(r => ({ type: 'restaurant', text: r.name, id: r.id, city: r.city }));
      
      newSuggestions.push(...matchedByCity);

      // Match cuisines
      const matchedCuisines = cuisineTypes
        .filter(c => c.toLowerCase().includes(query))
        .slice(0, 3)
        .map(c => ({ type: 'cuisine', text: c }));
      
      newSuggestions.push(...matchedCuisines);

      // Match popular searches (only if few matches)
      if (newSuggestions.length < 3) {
        const matchedPopular = POPULAR_SEARCHES
          .filter(p => p.toLowerCase().includes(query) && !newSuggestions.find(s => s.text.toLowerCase() === p.toLowerCase()))
          .slice(0, 2)
          .map(p => ({ type: 'popular', text: p }));
        
        newSuggestions.push(...matchedPopular);
      }

      setSuggestions(newSuggestions.slice(0, 8));

      // Also fetch dish suggestions from API
      if (query.length >= 2) {
        try {
          const response = await axios.get(`${API}/restaurants/search/dishes?q=${encodeURIComponent(query)}&limit=5`);
          setDishSuggestions(response.data.slice(0, 5));
        } catch (error) {
          console.error('Error fetching dish suggestions:', error);
          setDishSuggestions([]);
        }
      } else {
        setDishSuggestions([]);
      }

      setIsSearching(false);
    }, 200); // 200ms debounce

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, allRestaurants, restaurants, cuisineTypes]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveToHistory = (query) => {
    if (!query.trim()) return;
    
    const newHistory = [
      query,
      ...searchHistory.filter(h => h.toLowerCase() !== query.toLowerCase())
    ].slice(0, MAX_HISTORY_ITEMS);
    
    setSearchHistory(newHistory);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
  };

  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  };

  const removeFromHistory = (item, e) => {
    e.stopPropagation();
    const newHistory = searchHistory.filter(h => h !== item);
    setSearchHistory(newHistory);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      saveToHistory(searchQuery);
    }
    setShowSuggestions(false);
    onSearch();
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.text);
    saveToHistory(suggestion.text);
    setShowSuggestions(false);
    onSearch();
  };

  const handleHistoryClick = (query) => {
    setSearchQuery(query);
    setShowSuggestions(false);
    onSearch();
  };

  const handlePopularClick = (query) => {
    setSearchQuery(query);
    saveToHistory(query);
    setShowSuggestions(false);
    onSearch();
  };

  return (
    <div className="relative" ref={searchRef}>
      <form onSubmit={handleSearchSubmit} className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <Input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            placeholder="Rechercher un restaurant ou une cuisine..."
            className="pl-10 pr-10 h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                inputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <div className="relative hidden sm:block w-40">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            type="text"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            placeholder="Ville..."
            className="pl-9 h-12 rounded-xl border-gray-200"
          />
        </div>
        
        <Button 
          type="submit" 
          className="h-12 px-6 bg-emerald-500 hover:bg-emerald-600 rounded-xl"
        >
          <Search className="w-5 h-5 sm:hidden" />
          <span className="hidden sm:inline">Rechercher</span>
        </Button>
      </form>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden max-h-96 overflow-y-auto">
          
          {/* Loading state */}
          {isSearching && searchQuery.length > 0 && (
            <div className="p-4 flex items-center justify-center text-gray-500">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Recherche...
            </div>
          )}

          {/* Suggestions */}
          {!isSearching && suggestions.length > 0 && (
            <div className="p-2">
              <p className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase">Restaurants & Cuisines</p>
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}-${index}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg text-left transition-colors"
                >
                  {suggestion.type === 'restaurant' ? (
                    <>
                      <MapPin className="w-4 h-4 text-emerald-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{suggestion.text}</p>
                        {suggestion.city && (
                          <p className="text-xs text-gray-500">{suggestion.city}</p>
                        )}
                      </div>
                    </>
                  ) : suggestion.type === 'cuisine' ? (
                    <>
                      <TrendingUp className="w-4 h-4 text-blue-500 shrink-0" />
                      <span className="text-gray-700">{suggestion.text}</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-gray-700">{suggestion.text}</span>
                    </>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Dish Suggestions */}
          {!isSearching && dishSuggestions.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="flex items-center justify-between px-3 py-1.5">
                <p className="text-xs font-medium text-gray-500 uppercase">Plats</p>
                <button 
                  onClick={() => {
                    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                    setShowSuggestions(false);
                  }}
                  className="text-xs text-emerald-600 hover:text-emerald-700"
                >
                  Voir tous
                </button>
              </div>
              {dishSuggestions.map((dish) => (
                <button
                  key={dish.id}
                  onClick={() => {
                    navigate(`/restaurant/${dish.restaurant?.id}`);
                    setShowSuggestions(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg text-left transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                    {dish.image ? (
                      <img src={dish.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <UtensilsCrossed className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{dish.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {dish.restaurant?.name} • {dish.price?.toFixed(2)} €
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Link to full dish search */}
          {searchQuery.length >= 2 && !isSearching && (
            <div className="p-2 border-t border-gray-100">
              <button
                onClick={() => {
                  navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                  setShowSuggestions(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl text-left transition-colors"
              >
                <UtensilsCrossed className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-emerald-700">
                  Rechercher "{searchQuery}" dans les plats
                </span>
              </button>
            </div>
          )}

          {/* No suggestions found */}
          {!isSearching && searchQuery.length > 0 && suggestions.length === 0 && (
            <div className="p-4 text-center text-gray-500 text-sm">
              {"Aucune suggestion pour"} &quot;{searchQuery}&quot;
            </div>
          )}

          {/* Search History - only shown when input is empty or focused without query */}
          {!searchQuery && searchHistory.length > 0 && (
            <div className="p-2 border-b border-gray-100">
              <div className="flex items-center justify-between px-3 py-1.5">
                <p className="text-xs font-medium text-gray-500 uppercase">Recherches récentes</p>
                <button 
                  onClick={clearHistory}
                  className="text-xs text-emerald-600 hover:text-emerald-700"
                >
                  Effacer
                </button>
              </div>
              {searchHistory.slice(0, 5).map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleHistoryClick(item)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg group"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                  <button
                    onClick={(e) => removeFromHistory(item, e)}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </button>
              ))}
            </div>
          )}

          {/* Popular Searches - always shown when no query */}
          {!searchQuery && (
            <div className="p-2">
              <p className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase">Recherches populaires</p>
              <div className="flex flex-wrap gap-2 px-3 py-2">
                {POPULAR_SEARCHES.map((item) => (
                  <button
                    key={item}
                    onClick={() => handlePopularClick(item)}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-emerald-50 hover:text-emerald-700 rounded-full text-sm text-gray-700 transition-colors"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
